/* SIZESOFTX
=========================================================================================
* Descripción: Utilerías de cámara y selección visual para IA
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1        | 06/06/2026 | Lic. José Luis Aragón Cervantes  | Normalización cuadrada de recortes IA para cámaras distintas
 *      2        | 06/06/2026 | Lic. José Luis Aragón Cervantes  | Contención radial para evitar expansión hacia artículos vecinos
 *      3        | 06/06/2026 | Lic. José Luis Aragón Cervantes  | Contorno activo contráctil (createObjectSelectionFromCircularContour)
 * ---------------------------------------------------------------------------------------
*/

export const getVideoDisplayBox = (videoElement) => {
    const rect = videoElement?.getBoundingClientRect?.();
    if (!rect?.width || !rect?.height) {
        return {
            leftPx: 0,
            topPx: 0,
            widthPx: rect?.width || 1,
            heightPx: rect?.height || 1,
            xPct: 0,
            yPct: 0,
            widthPct: 1,
            heightPct: 1,
        };
    }

    const videoWidth = videoElement.videoWidth || 640;
    const videoHeight = videoElement.videoHeight || 480;
    const objectFit = window.getComputedStyle(videoElement).objectFit || "contain";
    const rectRatio = rect.width / rect.height;
    const videoRatio = videoWidth / videoHeight;
    let widthPx = rect.width;
    let heightPx = rect.height;

    if (objectFit === "contain") {
        if (videoRatio > rectRatio) {
            widthPx = rect.width;
            heightPx = rect.width / videoRatio;
        } else {
            heightPx = rect.height;
            widthPx = rect.height * videoRatio;
        }
    }

    if (objectFit === "cover") {
        if (videoRatio > rectRatio) {
            heightPx = rect.height;
            widthPx = rect.height * videoRatio;
        } else {
            widthPx = rect.width;
            heightPx = rect.width / videoRatio;
        }
    }

    const leftPx = (rect.width - widthPx) / 2;
    const topPx = (rect.height - heightPx) / 2;

    return {
        leftPx,
        topPx,
        widthPx,
        heightPx,
        xPct: leftPx / rect.width,
        yPct: topPx / rect.height,
        widthPct: widthPx / rect.width,
        heightPct: heightPx / rect.height,
    };
};

export const getCameraPoint = (event, videoElement) => {
    const rect = videoElement.getBoundingClientRect();
    const source = event.touches?.[0] || event.changedTouches?.[0] || event;
    const displayBox = getVideoDisplayBox(videoElement);
    const x = Math.max(0, Math.min(source.clientX - rect.left - displayBox.leftPx, displayBox.widthPx));
    const y = Math.max(0, Math.min(source.clientY - rect.top - displayBox.topPx, displayBox.heightPx));

    return {
        xPct: displayBox.widthPx ? x / displayBox.widthPx : 0.5,
        yPct: displayBox.heightPx ? y / displayBox.heightPx : 0.5,
    };
};

export const createObjectSelection = (event, videoElement, sizePct = 0.42) => {
    if (!videoElement) return null;

    const point = getCameraPoint(event, videoElement);
    const size = Math.max(0.18, Math.min(sizePct, 0.72));
    const widthPct = size;
    const heightPct = size;

    return {
        xPct: Math.max(0, Math.min(point.xPct - widthPct / 2, 1 - widthPct)),
        yPct: Math.max(0, Math.min(point.yPct - heightPct / 2, 1 - heightPct)),
        widthPct,
        heightPct,
    };
};

export const createObjectSelectionFromPoints = (startPoint, endPoint, minSizePct = 0.12) => {
    if (!startPoint || !endPoint) return null;

    const left = Math.max(0, Math.min(startPoint.xPct, endPoint.xPct));
    const top = Math.max(0, Math.min(startPoint.yPct, endPoint.yPct));
    const right = Math.min(1, Math.max(startPoint.xPct, endPoint.xPct));
    const bottom = Math.min(1, Math.max(startPoint.yPct, endPoint.yPct));
    const width = Math.max(right - left, minSizePct);
    const height = Math.max(bottom - top, minSizePct);

    return {
        xPct: Math.max(0, Math.min(left, 1 - width)),
        yPct: Math.max(0, Math.min(top, 1 - height)),
        widthPct: Math.min(width, 1),
        heightPct: Math.min(height, 1),
    };
};

export const createObjectSelectionFromLasso = (points, minSizePct = 0.08, isDrawing = false) => {
    if (!points?.length) return null;

    const xs = points.map(point => point.xPct);
    const ys = points.map(point => point.yPct);
    const left = Math.max(0, Math.min(...xs));
    const top = Math.max(0, Math.min(...ys));
    const right = Math.min(1, Math.max(...xs));
    const bottom = Math.min(1, Math.max(...ys));
    const width = Math.max(right - left, minSizePct);
    const height = Math.max(bottom - top, minSizePct);

    return {
        xPct: Math.max(0, Math.min(left, 1 - width)),
        yPct: Math.max(0, Math.min(top, 1 - height)),
        widthPct: Math.min(width, 1),
        heightPct: Math.min(height, 1),
        contourPoints: points.map(point => ({
            xPct: clamp01(point.xPct),
            yPct: clamp01(point.yPct),
        })),
        isDrawing,
    };
};

export const createFibonacciDebugRays = (seedPoint, radiusPct = 0.32, count = 64) => {
    if (!seedPoint) return [];

    const goldenAngle = Math.PI * (3 - Math.sqrt(5));
    const colors = [
        "#ff1744",
        "#ff9100",
        "#ffea00",
        "#00e676",
        "#00b0ff",
        "#651fff",
        "#f50057",
        "#00e5ff",
    ];

    return Array.from({ length: count }, (_, index) => {
        const normalizedRadius = Math.sqrt((index + 1) / count) * radiusPct;
        const angle = index * goldenAngle;
        return {
            x1Pct: clamp01(seedPoint.xPct),
            y1Pct: clamp01(seedPoint.yPct),
            x2Pct: clamp01(seedPoint.xPct + Math.cos(angle) * normalizedRadius),
            y2Pct: clamp01(seedPoint.yPct + Math.sin(angle) * normalizedRadius),
            color: colors[index % colors.length],
        };
    });
};

export const captureVideoFrame = ({
    videoElement,
    canvasElement,
    selection,
    mirror = false,
    quality = 0.82,
    normalize = true,
    targetSize = 320,
    paddingPct = 0.12,
    backgroundColor = "#ffffff",
}) => {
    if (!videoElement || !canvasElement) return null;

    const sourceWidth = videoElement.videoWidth || 640;
    const sourceHeight = videoElement.videoHeight || 480;
    if (!sourceWidth || !sourceHeight) return null;

    const sourceSelection = selection
        ? {
            ...selection,
            xPct: mirror ? 1 - selection.xPct - selection.widthPct : selection.xPct,
        }
        : null;
    const sx = sourceSelection ? Math.round(sourceSelection.xPct * sourceWidth) : 0;
    const sy = sourceSelection ? Math.round(sourceSelection.yPct * sourceHeight) : 0;
    const sw = Math.max(2, sourceSelection ? Math.round(sourceSelection.widthPct * sourceWidth) : sourceWidth);
    const sh = Math.max(2, sourceSelection ? Math.round(sourceSelection.heightPct * sourceHeight) : sourceHeight);

    if (normalize) {
        const normalizedSize = Math.max(160, Math.min(640, Number(targetSize) || 320));
        const safePaddingPct = Math.max(0, Math.min(0.32, Number(paddingPct) || 0));
        const drawableSize = normalizedSize * (1 - safePaddingPct * 2);
        const scale = Math.min(drawableSize / sw, drawableSize / sh);
        const drawWidth = Math.max(1, Math.round(sw * scale));
        const drawHeight = Math.max(1, Math.round(sh * scale));
        const dx = Math.round((normalizedSize - drawWidth) / 2);
        const dy = Math.round((normalizedSize - drawHeight) / 2);

        canvasElement.width = normalizedSize;
        canvasElement.height = normalizedSize;

        const normalizedContext = canvasElement.getContext("2d");
        if (!normalizedContext) return null;
        normalizedContext.save();
        normalizedContext.fillStyle = backgroundColor;
        normalizedContext.fillRect(0, 0, normalizedSize, normalizedSize);

        if (mirror) {
            normalizedContext.translate(normalizedSize, 0);
            normalizedContext.scale(-1, 1);
            normalizedContext.drawImage(videoElement, sx, sy, sw, sh, normalizedSize - dx - drawWidth, dy, drawWidth, drawHeight);
        } else {
            normalizedContext.drawImage(videoElement, sx, sy, sw, sh, dx, dy, drawWidth, drawHeight);
        }

        normalizedContext.restore();
        return canvasElement.toDataURL("image/jpeg", quality);
    }

    canvasElement.width = sw;
    canvasElement.height = sh;

    const context = canvasElement.getContext("2d");
    context.save();

    if (mirror) {
        context.translate(sw, 0);
        context.scale(-1, 1);
    }

    context.drawImage(videoElement, sx, sy, sw, sh, 0, 0, sw, sh);
    context.restore();

    return canvasElement.toDataURL("image/jpeg", quality);
};

const clamp01 = (value) => Math.max(0, Math.min(1, value));

const colorDistance = (a, b) => {
    const dr = a.r - b.r;
    const dg = a.g - b.g;
    const db = a.b - b.b;
    return Math.sqrt(dr * dr + dg * dg + db * db);
};

const getPixelColor = (data, width, x, y) => {
    const index = (y * width + x) * 4;
    return {
        r: data[index],
        g: data[index + 1],
        b: data[index + 2],
    };
};

const averageColors = (colors) => {
    if (!colors.length) return { r: 0, g: 0, b: 0 };

    const total = colors.reduce((acc, color) => ({
        r: acc.r + color.r,
        g: acc.g + color.g,
        b: acc.b + color.b,
    }), { r: 0, g: 0, b: 0 });

    return {
        r: total.r / colors.length,
        g: total.g / colors.length,
        b: total.b / colors.length,
    };
};

const getFrameImageData = ({ videoElement, mirror = false, width = 192 }) => {
    if (!videoElement) return null;

    const sourceWidth = videoElement.videoWidth || 640;
    const sourceHeight = videoElement.videoHeight || 480;
    if (!sourceWidth || !sourceHeight) return null;

    const height = Math.max(1, Math.round(width * (sourceHeight / sourceWidth)));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) return null;

    context.save();
    if (mirror) {
        context.translate(width, 0);
        context.scale(-1, 1);
    }
    context.drawImage(videoElement, 0, 0, width, height);
    context.restore();

    return {
        width,
        height,
        data: context.getImageData(0, 0, width, height).data,
    };
};

const getGrayFrame = ({ videoElement, mirror = false, width = 224 }) => {
    const frame = getFrameImageData({ videoElement, mirror, width });
    if (!frame) return null;

    const gray = new Uint8Array(frame.width * frame.height);
    for (let index = 0; index < frame.data.length; index += 4) {
        gray[index / 4] = Math.round(
            frame.data[index] * 0.299
            + frame.data[index + 1] * 0.587
            + frame.data[index + 2] * 0.114
        );
    }

    return {
        width: frame.width,
        height: frame.height,
        gray,
    };
};

const calculateMotionShift = ({ videoElement, currentSelection, template, mirror = false }) => {
    if (!videoElement || !currentSelection || !template) return null;

    const frame = getGrayFrame({ videoElement, mirror, width: 224 });
    const previous = template.previousGrayFrame;

    if (!frame) return null;

    template.previousGrayFrame = frame;

    if (!previous || previous.width !== frame.width || previous.height !== frame.height) {
        return null;
    }

    const paddingX = currentSelection.widthPct * 0.35;
    const paddingY = currentSelection.heightPct * 0.35;
    const left = Math.max(0, Math.floor((currentSelection.xPct - paddingX) * frame.width));
    const top = Math.max(0, Math.floor((currentSelection.yPct - paddingY) * frame.height));
    const right = Math.min(frame.width - 1, Math.ceil((currentSelection.xPct + currentSelection.widthPct + paddingX) * frame.width));
    const bottom = Math.min(frame.height - 1, Math.ceil((currentSelection.yPct + currentSelection.heightPct + paddingY) * frame.height));

    let weight = 0;
    let weightedX = 0;
    let weightedY = 0;
    const threshold = 16;

    for (let y = top; y <= bottom; y += 1) {
        for (let x = left; x <= right; x += 1) {
            const index = y * frame.width + x;
            const difference = Math.abs(frame.gray[index] - previous.gray[index]);

            if (difference > threshold) {
                const strongDifference = difference - threshold;
                weight += strongDifference;
                weightedX += x * strongDifference;
                weightedY += y * strongDifference;
            }
        }
    }

    const area = Math.max(1, (right - left + 1) * (bottom - top + 1));
    if (weight < area * 1.8) return null;

    const motionCenterX = weightedX / weight / frame.width;
    const motionCenterY = weightedY / weight / frame.height;
    const currentCenterX = currentSelection.xPct + currentSelection.widthPct / 2;
    const currentCenterY = currentSelection.yPct + currentSelection.heightPct / 2;

    return {
        dx: (motionCenterX - currentCenterX) * 0.55,
        dy: (motionCenterY - currentCenterY) * 0.55,
        confidence: Math.min(1, weight / (area * 14)),
    };
};

const findRayBoundaryT = (sx, sy, cos, sin) => {
    let tMax = Infinity;
    if (cos > 0) tMax = Math.min(tMax, (1 - sx) / cos);
    if (cos < 0) tMax = Math.min(tMax, -sx / cos);
    if (sin > 0) tMax = Math.min(tMax, (1 - sy) / sin);
    if (sin < 0) tMax = Math.min(tMax, -sy / sin);
    return tMax > 0 && tMax < Infinity ? tMax : null;
};

export const createObjectSelectionFromCircularContour = ({
    videoElement,
    seedPoint,
    mirror = false,
    rayCount = 72,
    maxRadiusPct = 0.30,
    minRadiusPct = 0.045,
    maxNeighborEscapePct = 0.14,
    smoothingPasses = 2,
}) => {
    if (!videoElement || !seedPoint) return null;

    const frame = getFrameImageData({ videoElement, mirror, width: 224 });
    if (!frame) return null;

    const seed = {
        xPct: clamp01(seedPoint.xPct),
        yPct: clamp01(seedPoint.yPct),
    };
    const seedX = Math.max(0, Math.min(frame.width - 1, Math.round(seed.xPct * frame.width)));
    const seedY = Math.max(0, Math.min(frame.height - 1, Math.round(seed.yPct * frame.height)));
    const seedColor = getPixelColor(frame.data, frame.width, seedX, seedY);
    const seedGray = Math.round(seedColor.r * 0.299 + seedColor.g * 0.587 + seedColor.b * 0.114);
    const radialPoints = [];
    const colors = ["#ff1744", "#ff9100", "#ffea00", "#00e676", "#00b0ff", "#651fff"];

    for (let index = 0; index < rayCount; index += 1) {
        const angle = (Math.PI * 2 * index) / rayCount;
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);

        const tMax = findRayBoundaryT(seed.xPct, seed.yPct, cos, sin);
        if (!tMax || tMax <= 0) continue;

        const numSteps = Math.max(10, Math.min(200, Math.round(tMax * frame.width)));
        const skipSteps = Math.min(4, Math.max(2, Math.round(numSteps * 0.04)));

        const graySamples = [];
        const seedDistSamples = [];

        for (let stepIndex = 0; stepIndex <= numSteps; stepIndex += 1) {
            const frac = stepIndex / numSteps;
            const t = tMax * frac;
            const xPct = clamp01(seed.xPct + cos * t);
            const yPct = clamp01(seed.yPct + sin * t);
            const x = Math.round(xPct * frame.width);
            const y = Math.round(yPct * frame.height);
            const currentColor = getPixelColor(frame.data, frame.width, x, y);
            const gray = Math.round(currentColor.r * 0.299 + currentColor.g * 0.587 + currentColor.b * 0.114);
            const seedDistance = colorDistance(currentColor, seedColor);

            graySamples.push(gray);
            seedDistSamples.push(seedDistance);
        }

        let bestScore = 0;
        let bestStep = -1;

        for (let stepIndex = skipSteps + 1; stepIndex <= numSteps; stepIndex += 1) {
            const gradient = Math.abs(graySamples[stepIndex] - graySamples[stepIndex - 1]);
            const seedDistance = seedDistSamples[stepIndex];
            const drift = Math.abs(graySamples[stepIndex] - seedGray);

            const score = (gradient * 1.0) + (seedDistance * 0.30) + (drift * 0.60);

            if (score > bestScore && gradient >= 8 && seedDistance >= 30) {
                bestScore = score;
                bestStep = stepIndex;
            }
        }

        let bestRadiusPct;

        if (bestStep >= 0) {
            const refineStart = Math.max(1, bestStep - 1);
            const refineEnd = Math.min(numSteps, bestStep + 1);
            let bestRefineScore = 0;
            let bestRefineStep = bestStep;

            for (let si = refineStart; si <= refineEnd; si += 1) {
                const gradient = Math.abs(graySamples[si] - graySamples[si - 1]);
                const seedDistance = seedDistSamples[si];
                const drift = Math.abs(graySamples[si] - seedGray);
                const score = (gradient * 1.0) + (seedDistance * 0.30) + (drift * 0.60);

                if (score > bestRefineScore) {
                    bestRefineScore = score;
                    bestRefineStep = si;
                }
            }

            const boundaryStep = Math.max(0.5, bestRefineStep - 0.5);
            const frac = boundaryStep / numSteps;
            const t = tMax * frac;
            const rxPct = clamp01(seed.xPct + cos * t);
            const ryPct = clamp01(seed.yPct + sin * t);
            const dx = rxPct - seed.xPct;
            const dy = ryPct - seed.yPct;
            bestRadiusPct = Math.sqrt(dx * dx + dy * dy);
        } else {
            bestRadiusPct = maxRadiusPct;
        }

        radialPoints.push({
            angle, cos, sin,
            radiusPct: bestRadiusPct,
            color: colors[index % colors.length],
        });
    }

    if (radialPoints.length < 8) return null;

    const clampRadius = (r) => Math.max(minRadiusPct, Math.min(maxRadiusPct, r));

    let adjustedRadii = radialPoints.map(point => clampRadius(point.radiusPct));

    adjustedRadii = adjustedRadii.map((radiusPct, index, radii) => {
        const prev = radii[(index - 1 + radii.length) % radii.length];
        const next = radii[(index + 1) % radii.length];
        const neighborLimit = Math.max(prev, next) + maxNeighborEscapePct;
        return clampRadius(Math.min(radiusPct, neighborLimit));
    });

    for (let pass = 0; pass < smoothingPasses; pass += 1) {
        adjustedRadii = adjustedRadii.map((radiusPct, index, radii) => {
            const prev = radii[(index - 1 + radii.length) % radii.length];
            const next = radii[(index + 1) % radii.length];
            const smoothed = (prev * 0.22) + (radiusPct * 0.56) + (next * 0.22);
            return clampRadius(smoothed);
        });
    }

    const points = radialPoints.map((point, index) => ({
        xPct: clamp01(seed.xPct + point.cos * adjustedRadii[index]),
        yPct: clamp01(seed.yPct + point.sin * adjustedRadii[index]),
    }));
    const debugRays = radialPoints.map((point, index) => ({
        x1Pct: clamp01(seed.xPct + point.cos * maxRadiusPct * 1.1),
        y1Pct: clamp01(seed.yPct + point.sin * maxRadiusPct * 1.1),
        x2Pct: points[index].xPct,
        y2Pct: points[index].yPct,
        color: point.color,
    }));

    const selection = createObjectSelectionFromLasso(points, 0.08, false);
    if (!selection) return null;

    return {
        ...selection,
        debugRays,
    };
};

const getSourceSelection = (selection, mirror = false) => {
    if (!selection) return null;

    return {
        ...selection,
        xPct: mirror ? 1 - selection.xPct - selection.widthPct : selection.xPct,
    };
};

export const createSelectionDebugPreview = ({ videoElement, selection, mirror = false, size = 96 }) => {
    if (!videoElement || !selection || selection.widthPct <= 0 || selection.heightPct <= 0) return null;

    const sourceWidth = videoElement.videoWidth || 640;
    const sourceHeight = videoElement.videoHeight || 480;
    if (!sourceWidth || !sourceHeight) return null;

    const sourceSelection = getSourceSelection(selection, mirror);
    const sx = Math.round(sourceSelection.xPct * sourceWidth);
    const sy = Math.round(sourceSelection.yPct * sourceHeight);
    const sw = Math.max(2, Math.round(sourceSelection.widthPct * sourceWidth));
    const sh = Math.max(2, Math.round(sourceSelection.heightPct * sourceHeight));
    const previewSize = Math.max(48, Math.min(160, size));

    const sourceCanvas = document.createElement("canvas");
    sourceCanvas.width = previewSize;
    sourceCanvas.height = previewSize;
    const sourceContext = sourceCanvas.getContext("2d", { willReadFrequently: true });
    if (!sourceContext) return null;

    sourceContext.drawImage(videoElement, sx, sy, sw, sh, 0, 0, previewSize, previewSize);
    const sourceImage = sourceContext.getImageData(0, 0, previewSize, previewSize);
    const grayImage = sourceContext.createImageData(previewSize, previewSize);
    const edgeImage = sourceContext.createImageData(previewSize, previewSize);
    const gray = new Uint8Array(previewSize * previewSize);

    for (let index = 0; index < sourceImage.data.length; index += 4) {
        const grayValue = Math.round(
            sourceImage.data[index] * 0.299
            + sourceImage.data[index + 1] * 0.587
            + sourceImage.data[index + 2] * 0.114
        );
        const pixelIndex = index / 4;
        gray[pixelIndex] = grayValue;
        grayImage.data[index] = grayValue;
        grayImage.data[index + 1] = grayValue;
        grayImage.data[index + 2] = grayValue;
        grayImage.data[index + 3] = 255;
    }

    let edgeWeight = 0;
    let weightedX = 0;
    let weightedY = 0;
    for (let y = 0; y < previewSize; y += 1) {
        for (let x = 0; x < previewSize; x += 1) {
            const index = y * previewSize + x;
            const right = x < previewSize - 1 ? gray[index + 1] : gray[index];
            const bottom = y < previewSize - 1 ? gray[index + previewSize] : gray[index];
            const edge = Math.min(255, Math.abs(gray[index] - right) + Math.abs(gray[index] - bottom));
            const dataIndex = index * 4;

            edgeImage.data[dataIndex] = edge;
            edgeImage.data[dataIndex + 1] = edge;
            edgeImage.data[dataIndex + 2] = edge;
            edgeImage.data[dataIndex + 3] = 255;
            edgeWeight += edge;
            weightedX += x * edge;
            weightedY += y * edge;
        }
    }

    const grayCanvas = document.createElement("canvas");
    grayCanvas.width = previewSize;
    grayCanvas.height = previewSize;
    const grayContext = grayCanvas.getContext("2d");
    grayContext.putImageData(grayImage, 0, 0);

    const edgeCanvas = document.createElement("canvas");
    edgeCanvas.width = previewSize;
    edgeCanvas.height = previewSize;
    const edgeContext = edgeCanvas.getContext("2d");
    edgeContext.putImageData(edgeImage, 0, 0);

    return {
        grayDataUrl: grayCanvas.toDataURL("image/jpeg", 0.78),
        edgeDataUrl: edgeCanvas.toDataURL("image/jpeg", 0.78),
        centerXPct: edgeWeight ? weightedX / edgeWeight / previewSize : 0.5,
        centerYPct: edgeWeight ? weightedY / edgeWeight / previewSize : 0.5,
        edgeWeight: Math.round(edgeWeight / (previewSize * previewSize)),
    };
};

const getSelectionImageData = ({ videoElement, selection, mirror = false, sampleSize = 14 }) => {
    if (!videoElement || !selection) return null;

    const sourceWidth = videoElement.videoWidth || 640;
    const sourceHeight = videoElement.videoHeight || 480;
    if (!sourceWidth || !sourceHeight) return null;

    const sourceSelection = getSourceSelection(selection, mirror);
    const sx = Math.round(sourceSelection.xPct * sourceWidth);
    const sy = Math.round(sourceSelection.yPct * sourceHeight);
    const sw = Math.max(2, Math.round(sourceSelection.widthPct * sourceWidth));
    const sh = Math.max(2, Math.round(sourceSelection.heightPct * sourceHeight));

    const canvas = document.createElement("canvas");
    canvas.width = sampleSize;
    canvas.height = sampleSize;
    const context = canvas.getContext("2d", { willReadFrequently: true });

    if (!context) return null;

    context.drawImage(videoElement, sx, sy, sw, sh, 0, 0, sampleSize, sampleSize);
    return context.getImageData(0, 0, sampleSize, sampleSize).data;
};

const buildVisualSignature = (imageData) => {
    if (!imageData) return null;

    const signature = [];
    for (let index = 0; index < imageData.length; index += 4) {
        const r = imageData[index];
        const g = imageData[index + 1];
        const b = imageData[index + 2];
        signature.push(Math.round((r + g + b) / 3));
    }

    return signature;
};

const getSelectionEdgeSignature = ({ videoElement, selection, mirror = false, sampleSize = 18 }) => {
    if (!videoElement || !selection) return null;

    const sourceWidth = videoElement.videoWidth || 640;
    const sourceHeight = videoElement.videoHeight || 480;
    if (!sourceWidth || !sourceHeight) return null;

    const sourceSelection = getSourceSelection(selection, mirror);
    const sx = Math.round(sourceSelection.xPct * sourceWidth);
    const sy = Math.round(sourceSelection.yPct * sourceHeight);
    const sw = Math.max(2, Math.round(sourceSelection.widthPct * sourceWidth));
    const sh = Math.max(2, Math.round(sourceSelection.heightPct * sourceHeight));
    const canvas = document.createElement("canvas");
    canvas.width = sampleSize;
    canvas.height = sampleSize;
    const context = canvas.getContext("2d", { willReadFrequently: true });

    if (!context) return null;

    context.drawImage(videoElement, sx, sy, sw, sh, 0, 0, sampleSize, sampleSize);
    const data = context.getImageData(0, 0, sampleSize, sampleSize).data;
    const gray = new Array(sampleSize * sampleSize);

    for (let index = 0; index < data.length; index += 4) {
        gray[index / 4] = Math.round(data[index] * 0.299 + data[index + 1] * 0.587 + data[index + 2] * 0.114);
    }

    const signature = [];
    let weightedX = 0;
    let weightedY = 0;
    let totalWeight = 0;

    for (let y = 0; y < sampleSize - 1; y += 1) {
        for (let x = 0; x < sampleSize - 1; x += 1) {
            const current = gray[y * sampleSize + x];
            const edge = Math.abs(current - gray[y * sampleSize + x + 1])
                + Math.abs(current - gray[(y + 1) * sampleSize + x]);
            const normalizedEdge = Math.min(255, edge);
            signature.push(normalizedEdge);
            weightedX += x * normalizedEdge;
            weightedY += y * normalizedEdge;
            totalWeight += normalizedEdge;
        }
    }

    return {
        signature,
        centerXPct: totalWeight ? (weightedX / totalWeight) / sampleSize : 0.5,
        centerYPct: totalWeight ? (weightedY / totalWeight) / sampleSize : 0.5,
    };
};

const getSelectionColorHistogram = ({ videoElement, selection, mirror = false, sampleSize = 24, bins = 4 }) => {
    if (!videoElement || !selection) return null;

    const sourceWidth = videoElement.videoWidth || 640;
    const sourceHeight = videoElement.videoHeight || 480;
    if (!sourceWidth || !sourceHeight) return null;

    const sourceSelection = getSourceSelection(selection, mirror);
    const sx = Math.round(sourceSelection.xPct * sourceWidth);
    const sy = Math.round(sourceSelection.yPct * sourceHeight);
    const sw = Math.max(2, Math.round(sourceSelection.widthPct * sourceWidth));
    const sh = Math.max(2, Math.round(sourceSelection.heightPct * sourceHeight));
    const canvas = document.createElement("canvas");
    canvas.width = sampleSize;
    canvas.height = sampleSize;
    const context = canvas.getContext("2d", { willReadFrequently: true });

    if (!context) return null;

    context.drawImage(videoElement, sx, sy, sw, sh, 0, 0, sampleSize, sampleSize);
    const data = context.getImageData(0, 0, sampleSize, sampleSize).data;
    const histogram = new Array(bins * bins * bins).fill(0);
    let total = 0;

    for (let index = 0; index < data.length; index += 4) {
        const rBin = Math.min(bins - 1, Math.floor((data[index] / 256) * bins));
        const gBin = Math.min(bins - 1, Math.floor((data[index + 1] / 256) * bins));
        const bBin = Math.min(bins - 1, Math.floor((data[index + 2] / 256) * bins));
        histogram[(rBin * bins * bins) + (gBin * bins) + bBin] += 1;
        total += 1;
    }

    return total ? histogram.map(value => value / total) : null;
};

const compareHistograms = (baseHistogram, candidateHistogram) => {
    if (!baseHistogram || !candidateHistogram || baseHistogram.length !== candidateHistogram.length) {
        return Number.MAX_SAFE_INTEGER;
    }

    let difference = 0;
    for (let index = 0; index < baseHistogram.length; index += 1) {
        difference += Math.abs(baseHistogram[index] - candidateHistogram[index]);
    }

    return difference * 255;
};

const compareSignatures = (baseSignature, candidateSignature) => {
    if (!baseSignature || !candidateSignature || baseSignature.length !== candidateSignature.length) {
        return Number.MAX_SAFE_INTEGER;
    }

    let difference = 0;
    for (let index = 0; index < baseSignature.length; index += 1) {
        difference += Math.abs(baseSignature[index] - candidateSignature[index]);
    }

    return difference / baseSignature.length;
};

const getAverageColorAroundPoint = (frame, point, radius = 2) => {
    if (!frame || !point) return null;

    const centerX = Math.max(0, Math.min(frame.width - 1, Math.round(point.xPct * frame.width)));
    const centerY = Math.max(0, Math.min(frame.height - 1, Math.round(point.yPct * frame.height)));
    const colors = [];

    for (let y = centerY - radius; y <= centerY + radius; y += 1) {
        for (let x = centerX - radius; x <= centerX + radius; x += 1) {
            if (x >= 0 && x < frame.width && y >= 0 && y < frame.height) {
                colors.push(getPixelColor(frame.data, frame.width, x, y));
            }
        }
    }

    return averageColors(colors);
};

const buildContourSignature = ({ frame, contourPoints, radius = 2, maxPoints = 42 }) => {
    if (!frame || !contourPoints?.length) return null;

    const step = Math.max(1, Math.floor(contourPoints.length / maxPoints));
    const signature = [];

    for (let index = 0; index < contourPoints.length; index += step) {
        const color = getAverageColorAroundPoint(frame, contourPoints[index], radius);
        if (color) {
            signature.push(Math.round(color.r), Math.round(color.g), Math.round(color.b));
        }
    }

    return signature.length ? signature : null;
};

const buildContourContrastSignature = ({ frame, contourPoints, centerPoint, radius = 2, offsetPct = 0.012, maxPoints = 42 }) => {
    if (!frame || !contourPoints?.length || !centerPoint) return null;

    const step = Math.max(1, Math.floor(contourPoints.length / maxPoints));
    const signature = [];

    for (let index = 0; index < contourPoints.length; index += step) {
        const point = contourPoints[index];
        const vx = point.xPct - centerPoint.xPct;
        const vy = point.yPct - centerPoint.yPct;
        const length = Math.max(0.0001, Math.sqrt(vx * vx + vy * vy));
        const nx = vx / length;
        const ny = vy / length;
        const insidePoint = {
            xPct: clamp01(point.xPct - nx * offsetPct),
            yPct: clamp01(point.yPct - ny * offsetPct),
        };
        const outsidePoint = {
            xPct: clamp01(point.xPct + nx * offsetPct),
            yPct: clamp01(point.yPct + ny * offsetPct),
        };
        const insideColor = getAverageColorAroundPoint(frame, insidePoint, radius);
        const outsideColor = getAverageColorAroundPoint(frame, outsidePoint, radius);

        if (insideColor && outsideColor) {
            signature.push(
                Math.round(insideColor.r - outsideColor.r),
                Math.round(insideColor.g - outsideColor.g),
                Math.round(insideColor.b - outsideColor.b)
            );
        }
    }

    return signature.length ? signature : null;
};

const getContourCenterPoint = (selection) => {
    const points = selection?.contourPoints;
    if (!points?.length) {
        return selection
            ? {
                xPct: selection.xPct + selection.widthPct / 2,
                yPct: selection.yPct + selection.heightPct / 2,
            }
            : null;
    }

    let area = 0;
    let centerX = 0;
    let centerY = 0;

    for (let index = 0; index < points.length; index += 1) {
        const current = points[index];
        const next = points[(index + 1) % points.length];
        const cross = current.xPct * next.yPct - next.xPct * current.yPct;
        area += cross;
        centerX += (current.xPct + next.xPct) * cross;
        centerY += (current.yPct + next.yPct) * cross;
    }

    if (Math.abs(area) < 0.00001) {
        return {
            xPct: points.reduce((total, point) => total + point.xPct, 0) / points.length,
            yPct: points.reduce((total, point) => total + point.yPct, 0) / points.length,
        };
    }

    return {
        xPct: clamp01(centerX / (3 * area)),
        yPct: clamp01(centerY / (3 * area)),
    };
};

const getRadiusToSelectionBounds = ({ selection, centerPoint, angle }) => {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const right = selection.xPct + selection.widthPct;
    const bottom = selection.yPct + selection.heightPct;
    const distances = [];

    if (Math.abs(cos) > 0.0001) {
        distances.push(((cos > 0 ? right : selection.xPct) - centerPoint.xPct) / cos);
    }
    if (Math.abs(sin) > 0.0001) {
        distances.push(((sin > 0 ? bottom : selection.yPct) - centerPoint.yPct) / sin);
    }

    const positiveDistances = distances.filter(distance => distance > 0);
    return positiveDistances.length
        ? Math.min(...positiveDistances)
        : Math.max(selection.widthPct, selection.heightPct) / 2;
};

const createRadialRaysFromContour = ({ selection, centerPoint, count = 30 }) => {
    if (!selection || !centerPoint) return [];

    const colors = ["#ff1744", "#ff9100", "#ffea00", "#00e676", "#00b0ff", "#651fff"];
    return Array.from({ length: count }, (_, index) => {
        const angle = (Math.PI * 2 * index) / count;
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        const contourDistances = selection.contourPoints?.map(point => {
            const dx = point.xPct - centerPoint.xPct;
            const dy = point.yPct - centerPoint.yPct;
            const projection = dx * cos + dy * sin;
            const perpendicular = Math.abs(dx * -sin + dy * cos);
            return { projection, perpendicular };
        }).filter(item => item.projection > 0);
        const nearestContour = contourDistances?.length
            ? contourDistances.reduce((best, item) => (item.perpendicular < best.perpendicular ? item : best), contourDistances[0])
            : null;
        const boundsDistance = getRadiusToSelectionBounds({ selection, centerPoint, angle });
        const distancePct = nearestContour && nearestContour.perpendicular < Math.max(selection.widthPct, selection.heightPct) * 0.12
            ? Math.max(0.01, nearestContour.projection)
            : Math.max(0.01, boundsDistance);

        return {
            angle,
            distancePct,
            color: colors[index % colors.length],
        };
    });
};

const buildRadialFingerprint = ({ frame, centerPoint, radialRays, samplesPerRay = 5 }) => {
    if (!frame || !centerPoint || !radialRays?.length) return null;

    const signature = [];

    radialRays.forEach((ray) => {
        let previousGray = null;

        for (let sample = 1; sample <= samplesPerRay; sample += 1) {
            const ratio = sample / samplesPerRay;
            const point = {
                xPct: clamp01(centerPoint.xPct + Math.cos(ray.angle) * ray.distancePct * ratio),
                yPct: clamp01(centerPoint.yPct + Math.sin(ray.angle) * ray.distancePct * ratio),
            };
            const color = getAverageColorAroundPoint(frame, point, 1);
            const gray = color ? Math.round(color.r * 0.299 + color.g * 0.587 + color.b * 0.114) : 0;
            const edge = previousGray === null ? 0 : Math.abs(gray - previousGray);

            signature.push(
                Math.round(color?.r ?? 0),
                Math.round(color?.g ?? 0),
                Math.round(color?.b ?? 0),
                gray,
                edge
            );
            previousGray = gray;
        }
    });

    return signature;
};

const createRadialDebugRays = ({ centerPoint, radialRays }) => {
    if (!centerPoint || !radialRays?.length) return [];

    return radialRays.map((ray) => ({
        x1Pct: centerPoint.xPct,
        y1Pct: centerPoint.yPct,
        x2Pct: clamp01(centerPoint.xPct + Math.cos(ray.angle) * ray.distancePct),
        y2Pct: clamp01(centerPoint.yPct + Math.sin(ray.angle) * ray.distancePct),
        color: ray.color,
    }));
};

export const createTrackingTemplate = ({ videoElement, selection, mirror = false }) => {
    const imageData = getSelectionImageData({ videoElement, selection, mirror });
    const signature = buildVisualSignature(imageData);

    if (!signature) return null;

    const frame = getFrameImageData({ videoElement, mirror, width: 224 });
    const edgeTemplate = getSelectionEdgeSignature({ videoElement, selection, mirror, sampleSize: 18 });
    const colorHistogram = getSelectionColorHistogram({ videoElement, selection, mirror, sampleSize: 24, bins: 4 });
    const contourSignature = selection.contourPoints?.length
        ? buildContourSignature({ frame, contourPoints: selection.contourPoints, radius: 2 })
        : null;
    const contourCenter = selection.contourPoints?.length
        ? {
            xPct: selection.xPct + selection.widthPct / 2,
            yPct: selection.yPct + selection.heightPct / 2,
        }
        : null;
    const contourContrastSignature = selection.contourPoints?.length
        ? buildContourContrastSignature({
            frame,
            contourPoints: selection.contourPoints,
            centerPoint: contourCenter,
            radius: 2,
            offsetPct: 0.012,
        })
        : null;
    const radialCenter = getContourCenterPoint(selection);
    const radialRays = selection.contourPoints?.length
        ? createRadialRaysFromContour({ selection, centerPoint: radialCenter, count: 30 })
        : null;
    const radialSignature = radialRays?.length
        ? buildRadialFingerprint({ frame, centerPoint: radialCenter, radialRays, samplesPerRay: 5 })
        : null;

    return {
        signature,
        edgeSignature: edgeTemplate?.signature || null,
        edgeCenter: edgeTemplate
            ? { xPct: edgeTemplate.centerXPct, yPct: edgeTemplate.centerYPct }
            : null,
        colorHistogram,
        contourSignature,
        contourContrastSignature,
        contourCenter,
        radialCenter,
        radialRays,
        radialSignature,
        radialDebugRays: createRadialDebugRays({ centerPoint: radialCenter, radialRays }),
        contourPointsCount: selection.contourPoints?.length || 0,
        widthPct: selection.widthPct,
        heightPct: selection.heightPct,
        previousGrayFrame: getGrayFrame({ videoElement, mirror, width: 224 }),
    };
};

export const createObjectMaskSelection = ({
    videoElement,
    selection,
    seedPoint,
    mirror = false,
}) => {
    if (!videoElement || !selection) return selection;

    const frame = getFrameImageData({ videoElement, mirror });
    if (!frame) return selection;

    const minX = Math.max(0, Math.floor(selection.xPct * frame.width));
    const minY = Math.max(0, Math.floor(selection.yPct * frame.height));
    const maxX = Math.min(frame.width - 1, Math.ceil((selection.xPct + selection.widthPct) * frame.width));
    const maxY = Math.min(frame.height - 1, Math.ceil((selection.yPct + selection.heightPct) * frame.height));

    if (maxX <= minX || maxY <= minY) return selection;

    const seedX = Math.max(minX, Math.min(maxX, Math.round((seedPoint?.xPct ?? selection.xPct + selection.widthPct / 2) * frame.width)));
    const seedY = Math.max(minY, Math.min(maxY, Math.round((seedPoint?.yPct ?? selection.yPct + selection.heightPct / 2) * frame.height)));
    const seedColor = getPixelColor(frame.data, frame.width, seedX, seedY);

    const borderColors = [];
    const stepBorder = Math.max(1, Math.floor((maxX - minX) / 24));
    for (let x = minX; x <= maxX; x += stepBorder) {
        borderColors.push(getPixelColor(frame.data, frame.width, x, minY));
        borderColors.push(getPixelColor(frame.data, frame.width, x, maxY));
    }
    const stepY = Math.max(1, Math.floor((maxY - minY) / 24));
    for (let y = minY; y <= maxY; y += stepY) {
        borderColors.push(getPixelColor(frame.data, frame.width, minX, y));
        borderColors.push(getPixelColor(frame.data, frame.width, maxX, y));
    }

    const backgroundColor = averageColors(borderColors);
    const seedBackgroundDistance = colorDistance(seedColor, backgroundColor);
    const seedThreshold = Math.max(42, Math.min(82, seedBackgroundDistance + 28));
    const backgroundThreshold = Math.max(30, Math.min(58, seedBackgroundDistance * 0.75));

    const mask = new Uint8Array(frame.width * frame.height);
    let foregroundCount = 0;

    for (let y = minY; y <= maxY; y += 1) {
        for (let x = minX; x <= maxX; x += 1) {
            const color = getPixelColor(frame.data, frame.width, x, y);
            const similarToSeed = colorDistance(color, seedColor) <= seedThreshold;
            const differentFromBackground = colorDistance(color, backgroundColor) >= backgroundThreshold;
            const isForeground = seedBackgroundDistance > 18
                ? (similarToSeed && differentFromBackground)
                : differentFromBackground;

            if (isForeground) {
                mask[y * frame.width + x] = 1;
                foregroundCount += 1;
            }
        }
    }

    const area = (maxX - minX + 1) * (maxY - minY + 1);
    if (foregroundCount < area * 0.03 || foregroundCount > area * 0.88) {
        return createObjectEnvelopeSelection(selection);
    }

    const topPoints = [];
    const bottomPoints = [];
    const columnStep = Math.max(1, Math.floor((maxX - minX) / 34));

    for (let x = minX; x <= maxX; x += columnStep) {
        let top = null;
        let bottom = null;

        for (let y = minY; y <= maxY; y += 1) {
            if (mask[y * frame.width + x]) {
                top = y;
                break;
            }
        }

        for (let y = maxY; y >= minY; y -= 1) {
            if (mask[y * frame.width + x]) {
                bottom = y;
                break;
            }
        }

        if (top !== null && bottom !== null) {
            topPoints.push({ xPct: x / frame.width, yPct: top / frame.height });
            bottomPoints.unshift({ xPct: x / frame.width, yPct: bottom / frame.height });
        }
    }

    const contourPoints = [...topPoints, ...bottomPoints];
    if (contourPoints.length < 6) return createObjectEnvelopeSelection(selection);

    const xs = contourPoints.map(point => point.xPct);
    const ys = contourPoints.map(point => point.yPct);
    const left = Math.min(...xs);
    const top = Math.min(...ys);
    const right = Math.max(...xs);
    const bottom = Math.max(...ys);
    const maskWidth = right - left;
    const maskHeight = bottom - top;

    if (maskWidth < selection.widthPct * 0.72 || maskHeight < selection.heightPct * 0.72) {
        return createObjectEnvelopeSelection(selection);
    }

    return {
        ...selection,
        xPct: Math.max(0, left),
        yPct: Math.max(0, top),
        widthPct: Math.min(1 - left, right - left),
        heightPct: Math.min(1 - top, bottom - top),
        contourPoints,
    };
};

export const createObjectEnvelopeSelection = (selection, paddingPct = 0.025) => {
    if (!selection) return null;

    const x = Math.max(0, selection.xPct - paddingPct);
    const y = Math.max(0, selection.yPct - paddingPct);
    const width = Math.min(1 - x, selection.widthPct + paddingPct * 2);
    const height = Math.min(1 - y, selection.heightPct + paddingPct * 2);
    const cx = x + width / 2;
    const cy = y + height / 2;
    const rx = width / 2;
    const ry = height / 2;
    const points = [];
    const totalPoints = 36;

    for (let index = 0; index < totalPoints; index += 1) {
        const angle = (Math.PI * 2 * index) / totalPoints;
        points.push({
            xPct: clamp01(cx + Math.cos(angle) * rx),
            yPct: clamp01(cy + Math.sin(angle) * ry),
        });
    }

    return {
        ...selection,
        xPct: x,
        yPct: y,
        widthPct: width,
        heightPct: height,
        contourPoints: points,
    };
};

export const createObjectSelectionFromSeedRays = ({
    videoElement,
    seedPoint,
    mirror = false,
    rayCount = 72,
    maxRadiusPct = 0.30,
    minRadiusPct = 0.045,
    maxNeighborEscapePct = 0.14,
    smoothingPasses = 2,
}) => {
    if (!videoElement || !seedPoint) return null;

    const frame = getFrameImageData({ videoElement, mirror, width: 224 });
    if (!frame) return null;

    const seed = {
        xPct: clamp01(seedPoint.xPct),
        yPct: clamp01(seedPoint.yPct),
    };
    const seedX = Math.max(0, Math.min(frame.width - 1, Math.round(seed.xPct * frame.width)));
    const seedY = Math.max(0, Math.min(frame.height - 1, Math.round(seed.yPct * frame.height)));
    const seedColor = getPixelColor(frame.data, frame.width, seedX, seedY);
    const radialPoints = [];
    const colors = ["#ff1744", "#ff9100", "#ffea00", "#00e676", "#00b0ff", "#651fff"];
    const edgeThreshold = 64;
    const backgroundDistanceThreshold = 88;
    const fallbackRadiusPct = Math.min(maxRadiusPct, Math.max(minRadiusPct, 0.16));

    for (let index = 0; index < rayCount; index += 1) {
        const angle = (Math.PI * 2 * index) / rayCount;
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        let previousColor = seedColor;
        let previousGray = Math.round(seedColor.r * 0.299 + seedColor.g * 0.587 + seedColor.b * 0.114);
        let accumulatedDistance = 0;
        let bestRadiusPct = minRadiusPct;
        let bestScore = 0;
        let backgroundHits = 0;
        const steps = 64;

        for (let stepIndex = 1; stepIndex <= steps; stepIndex += 1) {
            const radiusPct = minRadiusPct + (maxRadiusPct - minRadiusPct) * (stepIndex / steps);
            const xPct = clamp01(seed.xPct + cos * radiusPct);
            const yPct = clamp01(seed.yPct + sin * radiusPct);
            const x = Math.max(0, Math.min(frame.width - 1, Math.round(xPct * frame.width)));
            const y = Math.max(0, Math.min(frame.height - 1, Math.round(yPct * frame.height)));
            const currentColor = getPixelColor(frame.data, frame.width, x, y);
            const gray = Math.round(currentColor.r * 0.299 + currentColor.g * 0.587 + currentColor.b * 0.114);
            const seedDistance = colorDistance(currentColor, seedColor);
            const stepDistance = colorDistance(currentColor, previousColor);
            const grayDistance = Math.abs(gray - previousGray);

            accumulatedDistance += stepDistance * 0.18;
            const edgeScore = (seedDistance * 0.48) + (stepDistance * 1.15) + (grayDistance * 1.35) + (accumulatedDistance * 0.08);

            if (stepIndex > 6 && edgeScore > bestScore && edgeScore > edgeThreshold) {
                bestScore = edgeScore;
                bestRadiusPct = radiusPct;
            }

            if (stepIndex > 8 && seedDistance > backgroundDistanceThreshold) {
                backgroundHits += 1;
                if (backgroundHits >= 2) {
                    bestRadiusPct = Math.max(minRadiusPct, radiusPct - ((maxRadiusPct - minRadiusPct) / steps));
                    break;
                }
            } else {
                backgroundHits = 0;
            }

            previousColor = currentColor;
            previousGray = gray;
        }

        radialPoints.push({
            angle,
            radiusPct: bestScore > 0 || backgroundHits > 0 ? bestRadiusPct : fallbackRadiusPct,
            color: colors[index % colors.length],
        });
    }

    if (radialPoints.length < 8) return null;

    const clampRadius = (radiusPct) => Math.max(minRadiusPct, Math.min(maxRadiusPct, radiusPct));
    let adjustedRadii = radialPoints.map(point => clampRadius(point.radiusPct));

    adjustedRadii = adjustedRadii.map((radiusPct, index, radii) => {
        const prev = radii[(index - 1 + radii.length) % radii.length];
        const next = radii[(index + 1) % radii.length];
        const neighborLimit = Math.max(prev, next) + maxNeighborEscapePct;
        return clampRadius(Math.min(radiusPct, neighborLimit));
    });

    for (let pass = 0; pass < smoothingPasses; pass += 1) {
        adjustedRadii = adjustedRadii.map((radiusPct, index, radii) => {
            const prev = radii[(index - 1 + radii.length) % radii.length];
            const next = radii[(index + 1) % radii.length];
            const smoothed = (prev * 0.22) + (radiusPct * 0.56) + (next * 0.22);
            return clampRadius(smoothed);
        });
    }

    const points = radialPoints.map((radialPoint, index) => ({
        xPct: clamp01(seed.xPct + Math.cos(radialPoint.angle) * adjustedRadii[index]),
        yPct: clamp01(seed.yPct + Math.sin(radialPoint.angle) * adjustedRadii[index]),
    }));
    const debugRays = radialPoints.map((radialPoint, index) => ({
        x1Pct: seed.xPct,
        y1Pct: seed.yPct,
        x2Pct: points[index].xPct,
        y2Pct: points[index].yPct,
        color: radialPoint.color,
    }));

    const selection = createObjectSelectionFromLasso(points, 0.08, false);
    if (!selection) return null;

    return {
        ...selection,
        debugRays,
    };
};

export const trackObjectSelection = ({
    videoElement,
    currentSelection,
    template,
    mirror = false,
    searchRadiusPct = 0.08,
    searchSteps = 4,
}) => {
    if (!videoElement || !currentSelection || !template?.signature) return currentSelection;

    const widthPct = currentSelection.widthPct;
    const heightPct = currentSelection.heightPct;
    const centerX = currentSelection.xPct + widthPct / 2;
    const centerY = currentSelection.yPct + heightPct / 2;
    const step = searchRadiusPct / Math.max(1, searchSteps);

    let bestSelection = currentSelection;
    let bestDifference = Number.MAX_SAFE_INTEGER;
    const frame = template.contourSignature || template.radialSignature
        ? getFrameImageData({ videoElement, mirror, width: 224 })
        : null;
    const currentRadialCenter = getContourCenterPoint(currentSelection);

    for (let yStep = -searchSteps; yStep <= searchSteps; yStep += 1) {
        for (let xStep = -searchSteps; xStep <= searchSteps; xStep += 1) {
            const candidate = {
                xPct: clamp01(centerX + xStep * step - widthPct / 2),
                yPct: clamp01(centerY + yStep * step - heightPct / 2),
                widthPct,
                heightPct,
            };

            candidate.xPct = Math.min(candidate.xPct, 1 - widthPct);
            candidate.yPct = Math.min(candidate.yPct, 1 - heightPct);

            const deltaX = candidate.xPct - currentSelection.xPct;
            const deltaY = candidate.yPct - currentSelection.yPct;
            const candidateContourPoints = currentSelection.contourPoints?.map(point => ({
                xPct: clamp01(point.xPct + deltaX),
                yPct: clamp01(point.yPct + deltaY),
            }));
            const candidateCenter = {
                xPct: candidate.xPct + candidate.widthPct / 2,
                yPct: candidate.yPct + candidate.heightPct / 2,
            };
            const candidateRadialCenter = currentRadialCenter
                ? {
                    xPct: clamp01(currentRadialCenter.xPct + deltaX),
                    yPct: clamp01(currentRadialCenter.yPct + deltaY),
                }
                : candidateCenter;
            const radialDifference = template.radialSignature && template.radialRays
                ? compareSignatures(
                    template.radialSignature,
                    buildRadialFingerprint({
                        frame,
                        centerPoint: candidateRadialCenter,
                        radialRays: template.radialRays,
                        samplesPerRay: 5,
                    })
                )
                : Number.MAX_SAFE_INTEGER;

            if (template.radialSignature) {
                const difference = radialDifference;

                if (difference < bestDifference) {
                    bestDifference = difference;
                    bestSelection = currentSelection.contourPoints
                        ? {
                            ...candidate,
                            contourPoints: candidateContourPoints,
                            debugRays: createRadialDebugRays({
                                centerPoint: candidateRadialCenter,
                                radialRays: template.radialRays,
                            }),
                        }
                        : candidate;
                }
                continue;
            }

            const candidateEdgeTemplate = template.edgeSignature
                ? getSelectionEdgeSignature({ videoElement, selection: candidate, mirror, sampleSize: 18 })
                : null;
            const edgeDifference = candidateEdgeTemplate?.signature
                ? compareSignatures(template.edgeSignature, candidateEdgeTemplate.signature)
                : Number.MAX_SAFE_INTEGER;
            const colorHistogramDifference = template.colorHistogram
                ? compareHistograms(
                    template.colorHistogram,
                    getSelectionColorHistogram({ videoElement, selection: candidate, mirror, sampleSize: 24, bins: 4 })
                )
                : Number.MAX_SAFE_INTEGER;
            const centerDifference = candidateEdgeTemplate?.centerXPct !== undefined && template.edgeCenter
                ? (
                    Math.abs(template.edgeCenter.xPct - candidateEdgeTemplate.centerXPct)
                    + Math.abs(template.edgeCenter.yPct - candidateEdgeTemplate.centerYPct)
                ) * 255
                : Number.MAX_SAFE_INTEGER;
            const contrastDifference = template.contourContrastSignature && candidateContourPoints
                ? compareSignatures(
                    template.contourContrastSignature,
                    buildContourContrastSignature({
                        frame,
                        contourPoints: candidateContourPoints,
                        centerPoint: candidateCenter,
                        radius: 2,
                        offsetPct: 0.012,
                    })
                )
                : Number.MAX_SAFE_INTEGER;
            const contourDifference = template.contourSignature && candidateContourPoints
                ? compareSignatures(
                    template.contourSignature,
                    buildContourSignature({ frame, contourPoints: candidateContourPoints, radius: 2 })
                )
                : Number.MAX_SAFE_INTEGER;
            const candidateSignature = edgeDifference === Number.MAX_SAFE_INTEGER
                ? buildVisualSignature(getSelectionImageData({ videoElement, selection: candidate, mirror }))
                : null;
            const bodyDifference = candidateSignature
                ? compareSignatures(template.signature, candidateSignature)
                : Number.MAX_SAFE_INTEGER;
            const difference = edgeDifference !== Number.MAX_SAFE_INTEGER
                ? (
                    (radialDifference === Number.MAX_SAFE_INTEGER ? 0 : radialDifference * 0.54)
                    + edgeDifference * 0.22
                    + (colorHistogramDifference === Number.MAX_SAFE_INTEGER ? 0 : colorHistogramDifference * 0.16)
                    + (centerDifference === Number.MAX_SAFE_INTEGER ? 0 : centerDifference * 0.04)
                    + (contrastDifference === Number.MAX_SAFE_INTEGER ? 0 : contrastDifference * 0.025)
                    + (contourDifference === Number.MAX_SAFE_INTEGER ? 0 : contourDifference * 0.015)
                )
                : bodyDifference;

            if (difference < bestDifference) {
                bestDifference = difference;
                bestSelection = currentSelection.contourPoints
                    ? {
                        ...candidate,
                        contourPoints: candidateContourPoints,
                        debugRays: createRadialDebugRays({
                            centerPoint: candidateRadialCenter,
                            radialRays: template.radialRays,
                        }),
                    }
                    : candidate;
            }
        }
    }

    if (template.radialSignature && bestDifference > 62) {
        return currentSelection;
    }

    if (template.edgeSignature && !template.radialSignature && bestDifference > 56) {
        return currentSelection;
    }

    return bestSelection;
};
